/**
 * Wither summoning (vanilla WitherSkullBlock.checkSpawn), DOM-free.
 *
 *    S S S      S = wither skeleton skull
 *    # # #      # = soul sand / soul soil
 *    . # .      . = air
 *
 * Standing upright along X or Z. Placing the last skull completes the pattern: all seven blocks go
 * and a wither appears at the stem's cell (feet 0.55 above its floor) with its 220-tick spawn
 * sequence. Skulls on the floor only (this game has no wall skulls). Not in peaceful.
 */

import { AIR, SOUL_SAND_ID, SOUL_SOIL_ID, WITHER_SKULL_ID } from '../blocks';
import { Wither } from '../entities/boss/wither';
import type { Mob } from '../entities/mob';

export interface WitherPattern {
  /** The seven blocks to clear, flat [x, y, z, ...] (3 skulls, 3 base, 1 stem). */
  cells: number[];
  /** Feet of the new wither. */
  x: number; y: number; z: number;
  /** Which way the T runs: the wither faces across it. */
  axis: 'x' | 'z';
}

export const isWitherBase = (id: number): boolean => id === SOUL_SAND_ID || id === SOUL_SOIL_ID;

/**
 * Does placing a skull at (x, y, z) complete the T? Tries both axes and all three skull positions
 * of the top row. `null` when it does not (wrong block, missing piece, or a non-air corner).
 */
export function findWitherPattern(get: (x: number, y: number, z: number) => number, x: number, y: number, z: number): WitherPattern | null {
  if (get(x, y, z) !== WITHER_SKULL_ID) return null;
  for (const axis of ['x', 'z'] as const) {
    const ax = axis === 'x' ? 1 : 0, az = axis === 'x' ? 0 : 1;
    for (let k = -1; k <= 1; k++) {
      const cx = x - ax * k, cz = z - az * k; // centre column of the T
      let ok = true;
      for (let i = -1; i <= 1 && ok; i++) {
        const px = cx + ax * i, pz = cz + az * i;
        if (get(px, y, pz) !== WITHER_SKULL_ID || !isWitherBase(get(px, y - 1, pz))) ok = false;
        else if (i !== 0 && get(px, y - 2, pz) !== AIR) ok = false;
      }
      if (!ok || !isWitherBase(get(cx, y - 2, cz))) continue;
      const cells: number[] = [];
      for (let i = -1; i <= 1; i++) cells.push(cx + ax * i, y, cz + az * i, cx + ax * i, y - 1, cz + az * i);
      cells.push(cx, y - 2, cz);
      return { cells, x: cx + 0.5, y: y - 2 + 0.55, z: cz + 0.5, axis };
    }
  }
  return null;
}

export interface SummonEnv {
  getBlock(x: number, y: number, z: number): number;
  setBlock(x: number, y: number, z: number, id: number): void;
  spawn(type: 'wither', x: number, y: number, z: number): Mob | null;
  difficulty?(): 'peaceful' | 'easy' | 'normal' | 'hard';
}

/** Called after a block is placed: summons the wither when it completes the pattern. Returns it (or null). */
export function trySummonWither(env: SummonEnv, x: number, y: number, z: number): Wither | null {
  if (env.difficulty?.() === 'peaceful') return null;
  const pat = findWitherPattern(env.getBlock, x, y, z);
  if (!pat) return null;
  for (let i = 0; i < pat.cells.length; i += 3) env.setBlock(pat.cells[i], pat.cells[i + 1], pat.cells[i + 2], AIR);
  const m = env.spawn('wither', pat.x, pat.y, pat.z);
  if (!(m instanceof Wither)) return null;
  m.yaw = m.headYaw = pat.axis === 'x' ? 0 : Math.PI / 2;
  m.startSpawn();
  return m;
}
